import React, { createContext, useContext, ReactNode } from 'react';
import { ThemeProvider, createTheme, responsiveFontSizes, alpha, Components, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import type {} from '@mui/x-data-grid/themeAugmentation';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const softShadowLight = '0 15px 35px rgba(15, 23, 42, 0.08)';

const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: '#2563eb',
    light: '#3b82f6',
    dark: '#1d4ed8',
    contrastText: '#f8fafc',
  },
  secondary: {
    main: '#14b8a6',
    light: '#2dd4bf',
    dark: '#0f766e',
    contrastText: '#f0fdfa',
  },
  background: {
    default: '#f4f6fb',
    paper: '#ffffff',
  },
  text: {
    primary: '#0f172a',
    secondary: '#475569',
  },
};

const componentOverrides = () => ({
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        border: `1px solid ${alpha('#cbd5f5', 0.7)}`,
        boxShadow: softShadowLight,
        backgroundImage: 'none',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 18,
        border: `1px solid ${alpha('#cbd5f5', 0.85)}`,
        boxShadow: softShadowLight,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        background: '#0f172a',
        color: '#ffffff',
        borderBottom: `1px solid ${alpha('#64748b', 0.35)}`,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#ffffff',
        borderRight: `1px solid ${alpha('#cbd5f5', 0.65)}`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        borderRadius: 10,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 48,
      },
      indicator: {
        height: 3,
        borderRadius: 3,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 48,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottomColor: alpha('#e2e8f0', 0.7),
      },
      head: {
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        color: '#475569',
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: 'none',
        fontFamily: '"Inter", "Segoe UI", "Helvetica", "Arial", sans-serif',
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: alpha('#f8fafc', 0.9),
          borderBottom: `2px solid ${alpha('#e2e8f0', 0.9)}`,
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: '#475569',
        },
        '& .MuiDataGrid-cell': {
          borderBottomColor: alpha('#e2e8f0', 0.7),
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: alpha('#2563eb', 0.04),
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: `2px solid ${alpha('#e2e8f0', 0.9)}`,
        },
        '& .MuiDataGrid-overlay': {
          backgroundColor: alpha('#ffffff', 0.72),
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: '#f4f6fb',
        color: '#0f172a',
        transition: 'background-color 0.3s ease, color 0.3s ease',
      },
      '*::-webkit-scrollbar': {
        width: 8,
        height: 8,
      },
      '*::-webkit-scrollbar-track': {
        background: alpha('#e2e8f0', 0.4),
      },
      '*::-webkit-scrollbar-thumb': {
        background: alpha('#94a3b8', 0.8),
        borderRadius: 999,
      },
    },
  },
});

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Always use light mode
  const isDarkMode = false;
  const toggleTheme = () => {
    // No-op function for compatibility
  };

  const theme = responsiveFontSizes(
    createTheme({
      palette: lightPalette,
      shape: {
        borderRadius: 16,
      },
      typography: {
        fontFamily: '"Inter", "Segoe UI", "Helvetica", "Arial", sans-serif',
        h1: { fontSize: '2.6rem', fontWeight: 700 },
        h2: { fontSize: '2.1rem', fontWeight: 700 },
        h3: { fontSize: '1.8rem', fontWeight: 600 },
        h4: { fontSize: '1.5rem', fontWeight: 600 },
        h5: { fontSize: '1.2rem', fontWeight: 600 },
        h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: 0.4 },
        subtitle1: { fontWeight: 600 },
        button: { fontWeight: 600 },
      },
      components: componentOverrides() as Components<Omit<Theme, 'components'>>,
    })
  );

  const value = {
    isDarkMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
};
